import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  alpha,
  useTheme,
  Skeleton,
  IconButton,
  Tooltip
} from '@mui/material';
import { Refresh, Download, Fullscreen, InsertChartOutlined } from '@mui/icons-material';

const ChartContainer = ({ 
  title, 
  subtitle,
  children, 
  height = 400,
  action,
  loading = false,
  onRefresh,
  onExport,
  onFullscreen
}) => {
  const theme = useTheme();

  const headerId = React.useId();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark
    ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`
    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)';

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        background: cardBg,
        border: '1px solid',
        borderColor: 'rgba(255,255,255,0.6)',
        transition: 'all 0.25s ease',
        '&:hover': {
          boxShadow: isDark
            ? `0 10px 24px ${alpha(theme.palette.common.black, 0.5)}`
            : `0 10px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
          borderColor: 'rgba(255,255,255,0.9)'
        },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.light, 0.9)} 100%)`,
          borderRadius: '3px 3px 0 0'
        }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between',
          mb: 2
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              id={headerId}
              variant="h6" 
              component="h3"
              sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontWeight: 400 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {/* Toolbar */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onRefresh && (
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={onRefresh} aria-label="Refresh chart">
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onExport && (
              <Tooltip title="Export">
                <IconButton size="small" onClick={onExport} aria-label="Export chart">
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onFullscreen && (
              <Tooltip title="Fullscreen">
                <IconButton size="small" onClick={onFullscreen} aria-label="Fullscreen chart">
                  <Fullscreen fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {action && <Box sx={{ ml: 1 }}>{action}</Box>}
          </Box>
        </Box>

        {/* Chart Content */}
        <Box
          role="region"
          aria-labelledby={headerId}
          sx={{ 
            flex: 1,
            minHeight: height,
            height: height,
            display: 'grid',
            placeItems: 'center',
            position: 'relative',
            borderRadius: 2,
            border: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
            background: alpha(isDark ? theme.palette.background.default : theme.palette.background.paper, 0.4)
          }}
        >
          <Box sx={{ width: '100%', height: '100%', maxHeight: height }}>
            {loading ? (
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
            ) : (
              children || (
                <Box sx={{ textAlign: 'center', color: 'text.secondary', p: 3 }}>
                  <InsertChartOutlined sx={{ fontSize: 40, mb: 1, opacity: 0.6 }} />
                  <Typography variant="body2">No data available</Typography>
                </Box>
              )
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChartContainer; 